// frontend/src/utils/exportPdf.js
import jsPDF from 'jspdf';

/**
 * Exports all <canvas> charts found inside `rootEl` into a multi-page A4 PDF.
 * - Captures canvases directly via toDataURL() for crisp output.
 * - Pulls the chart title from the nearest MUI Paper's <h6>.
 */

async function toDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve) => {
    const fr = new FileReader();
    fr.onloadend = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  });
}
export async function exportChartsPdf({
  rootEl,
  fileName = 'period-reporting.pdf',
  headerTitle = 'Period reporting',
  headerSubtitle = '',
  logoUrl
}) {
  if (!rootEl) throw new Error('exportChartsPdf: rootEl is required');

  // Collect canvases (in DOM order)
  const canvases = Array.from(rootEl.querySelectorAll('canvas')).filter(
    (c) => c.width && c.height
  );

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = { top: 64, right: 36, bottom: 36, left: 36 };
  const contentW = pageW - margin.left - margin.right;

  // Header on first page
  let y = margin.top;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(headerTitle, margin.left, y);
  y += 18;

  // Normalize fancy punctuation so it renders cleanly in PDF (fixes "!'")
  const subtitleSafe = String(headerSubtitle || '').replace(/[–—→]/g, '-');
  if (subtitleSafe) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(subtitleSafe, margin.left, y);
    y += 14;
  }

  // Generation time (small, left-aligned under date range)
  pdf.setFontSize(9);
  const gen = `Generated ${new Date().toLocaleString()}`;
  pdf.text(gen, margin.left, y);
  y += 12;

  // Optional: draw logo on first page (top-left, above or beside title area)
  if (logoUrl) {
    try {
      const dataUrl = await toDataUrl(logoUrl);
      // ~120pt wide keeps it tidy; adjust height by aspect ratio as needed
      const logoW = 120;
      const logoH = 28; // tweak if your PNG has different proportions
      // Place near the top; bump y so charts don't overlap the logo
      pdf.addImage(dataUrl, 'PNG', pageW - margin.right - logoW, margin.top - 8, logoW, logoH, undefined, 'FAST');
    } catch (_) { /* fail-soft if logo can't load */ }
  }

  // A little spacer under header
  y += 12;

  // Helper: add a new page with header
  const addPageWithHeader = () => {
    pdf.addPage();
    y = margin.top;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
    pdf.text(headerTitle, margin.left, y);
    y += 18;
    if (headerSubtitle) {
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11);
      pdf.text(String(headerSubtitle), margin.left, y);
      y += 14;
    }
    y += 12;
  };

  // Render each chart
  for (const canvas of canvases) {
    // Find a human title: nearest MUI Paper root then its <h6>
    let titleText = '';
    const paper = canvas.closest('.MuiPaper-root');
    if (paper) {
      const h6 = paper.querySelector('h6, [role="heading"]');
      if (h6 && h6.textContent) titleText = h6.textContent.trim();
      if (!titleText) {
        const t = paper.querySelector('[data-pdf-title]');
        if (t) titleText = String(t.getAttribute('data-pdf-title') || '').trim();
      }
    }

    // If a title exists, place it before the image
    if (titleText) {
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
      const lineHeight = 14;
      const needed = lineHeight + 8; // title + spacer

      if (y + needed > pageH - margin.bottom) addPageWithHeader();
      pdf.text(titleText, margin.left, y);
      y += needed;
    }

    // Compute image size to fit width
    const imgW = contentW;
    const ratio = canvas.height / canvas.width;
    const imgH = Math.max(1, imgW * ratio);

    if (y + imgH > pageH - margin.bottom) addPageWithHeader();

    // Use the canvas directly for crisp PNG
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    pdf.addImage(dataUrl, 'PNG', margin.left, y, imgW, imgH, undefined, 'FAST');
    y += imgH + 18; // add space after each chart
  }

  // Footer on each page
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(180); // light grey
    const footer = 'CONFIDENTIAL';
    const w = pdf.getTextWidth(footer);
    pdf.text(footer, (pageW - w) / 2, pageH - (margin.bottom / 2));
  }
  pdf.setTextColor(0);

  pdf.save(fileName);
}

